import { FilterParams } from '../../utilities/http-params'
import {
  ActiveFilterDisplay,
  FilterMeta,
  QueryParam,
} from './common'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'

export type StringFilterState = Readonly<{
  values: readonly string[]
  config: Readonly<{
    meta: FilterMeta
    allowMultiple?: boolean
    constant?: boolean
    toParamValue?: (value: string) => string
    toFilterValue?: (paramValue: string) => string
  }>
}>

export const initialStringFilterState: StringFilterState = {
  values: [],
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}

type ActionMeta = Omit<FilterMeta, 'fieldDisplay'>
type ValuesPayload = Readonly<{ values: readonly string[] }>

export type SetStringFilterConfigAction = Readonly<{
  type: 'SET_STRING_FILTER_CONFIG_ACTION'
  payload: Pick<StringFilterState, 'config'>
  meta: ActionMeta
}>

export type AddStringsAction = Readonly<{
  type: 'ADD_STRINGS_ACTION'
  payload: ValuesPayload
  meta: ActionMeta
}>

export type RemoveStringsAction = Readonly<{
  type: 'REMOVE_STRINGS_ACTION'
  payload: ValuesPayload
  meta: ActionMeta
}>

export type SetStringsAction = Readonly<{
  type: 'SET_STRINGS_ACTION'
  payload: ValuesPayload
  meta: ActionMeta
}>

export type StringFilterAction =
  | SetStringFilterConfigAction
  | AddStringsAction
  | RemoveStringsAction
  | SetStringsAction

export const stringFilterReducer = (
  state: StringFilterState,
  action: StringFilterAction,
): StringFilterState => {
  switch (action.type) {
    case 'SET_STRING_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_STRINGS_ACTION':
      return {
        ...state,
        values: state.config.allowMultiple
          ? action.payload.values
          : [action.payload.values[0]].filter(Boolean),
      }
    case 'REMOVE_STRINGS_ACTION':
      return {
        ...state,
        values: state.values.filter(
          (sv) => !action.payload.values.some((av) => av === sv),
        ),
      }
    case 'ADD_STRINGS_ACTION':
      if (state.config.allowMultiple) {
        return {
          ...state,
          values: [
            ...state.values.filter(sv => !action.payload.values.includes(sv)),
            ...action.payload.values.filter((av) => !state.values.includes(av)),
          ],
        }
      }
      return {
        ...state,
        values:
          state.values[0] !== action.payload.values[0]
            ? [action.payload.values[0]].filter(Boolean)
            : [],
      }
    default:
      return state
  }
}

export class StringFilter {
  readonly type = 'STRING'

  static SetStringFilterConfigAction(
    payload: SetStringFilterConfigAction['payload'],
    meta: ActionMeta,
  ): SetStringFilterConfigAction {
    return { type: 'SET_STRING_FILTER_CONFIG_ACTION', payload, meta }
  }

  static AddStringsAction(
    payload: AddStringsAction['payload'],
    meta: ActionMeta,
  ): AddStringsAction {
    return { type: 'ADD_STRINGS_ACTION', payload, meta }
  }

  static RemoveStringsAction(
    payload: RemoveStringsAction['payload'],
    meta: ActionMeta,
  ): RemoveStringsAction {
    return { type: 'REMOVE_STRINGS_ACTION', payload, meta }
  }

  static SetStringsAction(
    payload: SetStringsAction['payload'],
    meta: ActionMeta,
  ): SetStringsAction {
    return { type: 'SET_STRINGS_ACTION', payload, meta }
  }

  state: StringFilterState

  constructor(
    partialStateWithConfig: Partial<StringFilterState> & Pick<StringFilterState, 'config'>,
    private dispatch: (action: StringFilterAction) => void,
  ) {
    this.state = { ...initialStringFilterState, ...partialStateWithConfig }
  }

  apply(action: StringFilterAction) {
    this.state = stringFilterReducer(this.state, action)
    return this
  }

  private toParamValues(): readonly string[] {
    const { toParamValue } = this.state.config
    if (toParamValue) {
      return this.state.values.map(toParamValue)
    } else {
      return this.state.values
    }
  }

  private toFilterValues(paramValues: readonly string[]): readonly string[] {
    const { toFilterValue } = this.state.config
    if (toFilterValue) {
      return paramValues.map(toFilterValue)
    } else {
      return paramValues
    }
  }

  asParams(): FilterParams[] {
    return this.state.values.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          values: [
            {
              operation: 'in=',
              value: [this.toParamValues()].join(','),
            },
          ],
        },
      ]
      : []
  }

  asActiveDisplay(): ActiveFilterDisplay[] {
    return this.state.values.map((sp) => ({
      headerName: this.state.config.meta.fieldDisplay,
      operation: '=',
      value: sp,
      constant: this.state.config.constant ?? false,
      remove: () =>
        this.dispatch(
          StringFilter.RemoveStringsAction(
            { values: [sp] },
            this.state.config.meta,
          ),
        ),
    }))
  }

  asCount() {
    return this.state.values.length
  }

  fromUrlQueryParams(
    params: readonly QueryParam[],
    defaultVal: readonly string[] | string | null | undefined,
  ) {
    const getValues = (): readonly string[] => {
      const queryParam = params.find(_ => _.name === this.state.config.meta.fieldName)
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const rawValue  = queryParam?.value || defaultVal
      if (Array.isArray(rawValue)) {
        return rawValue
      } else if (rawValue) {
        // seems like a compiler bug...
        return (rawValue as string).split(',')
      } else {
        return []
      }
    }

    this.dispatch(
      StringFilter.SetStringsAction(
        { values: this.toFilterValues(getValues()) },
        this.state.config.meta,
      ),
    )
  }

  asUrlQueryParams(): QueryParam[] {
    return this.state.values.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: this.toParamValues().join(','),
        },
      ]
      : []
  }

  asDashboardQueryParams(): QueryParam[] {
    return this.state.values.length
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: this.toParamValues()
            .map((sp) => encodeURIComponent(sp))
            .join(','),
        },
      ]
      : []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    const allowed_fields = [
      'service_point_group_name',
    ]
    const field = this.state.config.meta.fieldName
    return allowed_fields.includes(field)
      ? { [field]: this.state.values[0] || undefined }
      : {}  
  }
}
